import React, { useState, useEffect, useRef, useMemo } from 'react';
import api, { formatApiErrorDetail } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Zap, Trophy, Gift, Volume2, VolumeX, Maximize, Sparkles, RefreshCw, XCircle, CheckCircle } from 'lucide-react';



const wait = (milliseconds) =>
  new Promise((resolve) => setTimeout(resolve, milliseconds));

const shuffleItems = (items) => {
  const shuffled = [...items];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const target = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[target]] =
      [shuffled[target], shuffled[index]];
  }
  return shuffled;
};

const DigitalParticipantStage = ({
  phase,
  countdown,
  displayedParticipant,
  progress,
  total,
  selectedParticipant,
}) => {
  const visibleParticipant =
    selectedParticipant || displayedParticipant;
  const progressPercent = total > 0
    ? Math.min(100, (progress / total) * 100)
    : 0;

  return (
    <div
      className={`digital-draw-stage participant-digital-stage phase-${phase}`}
      data-testid="participant-digital-stage"
    >
      <div className="digital-stage-lights" aria-hidden="true" />

      {countdown && (
        <div className="digital-countdown" data-testid="draw-countdown">
          {countdown}
        </div>
      )}

      <div className="digital-console-label">
        {selectedParticipant ? 'WINNING PARTICIPANT' : 'PARTICIPANT SCANNER'}
      </div>

      <div className="digital-id-readout">
        <span>
          {selectedParticipant
            ? 'WINNING SUGO ID'
            : phase === 'participant-shuffle'
              ? 'SCANNING ACTIVE TICKETS'
              : '635 TICKETS READY'}
        </span>
        <strong data-testid="selected-sugo-id">
          {visibleParticipant?.sugo_id || '--------'}
        </strong>
        <small data-testid="selected-username">
          {selectedParticipant?.username ||
            visibleParticipant?.username ||
            'Waiting to begin'}
        </small>
      </div>

      <div className="digital-progress">
        <div>
          <span style={{ width: `${progressPercent}%` }} />
        </div>
        <strong>
          {phase === 'participant-shuffle'
            ? `${progress} / ${total} TICKETS PROCESSED`
            : selectedParticipant
              ? `SELECTED FROM ALL ${total} ACTIVE TICKETS`
              : `${total} INDIVIDUAL TICKETS LOADED`}
        </strong>
      </div>

      <div className="digital-scan-line" aria-hidden="true" />
    </div>
  );
};

const DigitalPrizeVault = ({
  phase,
  previewPrize,
  selectedPrize,
}) => {
  const visiblePrize = selectedPrize || previewPrize;
  const isShuffling = phase === 'prize-shuffle';
  const isRevealed = phase === 'reveal' && selectedPrize;

  return (
    <div
      className={`digital-draw-stage prize-digital-stage phase-${phase}`}
      data-testid="prize-digital-stage"
    >
      <div className="digital-stage-lights prize-stage-lights" aria-hidden="true" />

      <div className="digital-console-label">
        {isRevealed ? 'SELECTED PRIZE' : 'DIGITAL GIFT VAULT'}
      </div>

      <div
        className={`gift-vault ${isShuffling ? 'is-shaking' : ''} ${isRevealed ? 'is-open' : ''}`}
        aria-hidden="true"
      >
        <div className="gift-glow" />
        <div className="gift-box-lid">
          <span />
        </div>
        <div className="gift-box-body">
          <span />
        </div>
      </div>

      <div className={`digital-prize-readout ${isRevealed ? 'is-revealed' : ''}`}>
        {visiblePrize?.image_url ? (
          <img
            src={visiblePrize.image_url}
            alt={visiblePrize.name || 'Prize'}
            className="digital-prize-image"
          />
        ) : (
          <div className="digital-prize-placeholder">
            <Gift className="w-12 h-12" />
          </div>
        )}

        <strong data-testid="selected-prize">
          {visiblePrize?.name ||
            (phase === 'participant-shuffle'
              ? 'WAITING FOR PARTICIPANT'
              : 'PRIZES READY')}
        </strong>

        <small>
          {isShuffling
            ? 'SHUFFLING AVAILABLE PRIZES'
            : isRevealed
              ? 'GRAND PRIZE REVEAL'
              : visiblePrize?.remaining_quantity
                ? `${visiblePrize.remaining_quantity} AVAILABLE`
                : 'GIFT VAULT LOCKED'}
        </small>
      </div>

      <div className="prize-sparkles" aria-hidden="true">
        <i /><i /><i /><i /><i /><i />
      </div>
    </div>
  );
};

const LiveDraw = () => {
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawResult, setDrawResult] = useState(null);
  const [recentWinners, setRecentWinners] = useState([]);
  const [stats, setStats] = useState({ remaining_entries: 0, total_winners: 0 });
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try { const v = localStorage.getItem('livedraw_sound'); return v === null ? true : v === 'true'; } catch { return true; }
  });
  const [soundVolume, setSoundVolume] = useState(() => {
    try {
      const stored = Number(localStorage.getItem('livedraw_volume'));
      return Number.isFinite(stored) && stored >= 0 && stored <= 100
        ? stored
        : 100;
    } catch {
      return 100;
    }
  });
  const [confettiEnabled, setConfettiEnabled] = useState(() => {
    try { const v = localStorage.getItem('livedraw_confetti'); return v === null ? true : v === 'true'; } catch { return true; }
  });
  const [participantSpinning, setParticipantSpinning] = useState(false);
  const [prizeSpinning, setPrizeSpinning] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [selectedPrize, setSelectedPrize] = useState(null);
  const [participantEntries, setParticipantEntries] = useState([]);
  const [availablePrizes, setAvailablePrizes] = useState([]);
  const [wheelDataLoading, setWheelDataLoading] = useState(true);
  const [participantSearch, setParticipantSearch] = useState('');
  const [drawPhase, setDrawPhase] = useState('ready');
  const [countdownValue, setCountdownValue] = useState(null);
  const [participantDisplay, setParticipantDisplay] = useState(null);
  const [participantProgress, setParticipantProgress] = useState(0);
  const [prizePreview, setPrizePreview] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const confirmingRef = useRef(false);
  const redrawingRef = useRef(false);
  const cancellingRef = useRef(false);
  const startingRef = useRef(false);
  const audioContextRef = useRef(null);
  const audioMasterRef = useRef(null);

  const [redrawOpen, setRedrawOpen] = useState(false);
  const [redrawReason, setRedrawReason] = useState('');
  const [isRedrawSubmitting, setIsRedrawSubmitting] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelSubmitting, setIsCancelSubmitting] = useState(false);

  // Persist toggles
  useEffect(() => { try { localStorage.setItem('livedraw_sound', String(soundEnabled)); } catch {} }, [soundEnabled]);
  useEffect(() => { try { localStorage.setItem('livedraw_volume', String(soundVolume)); } catch {} }, [soundVolume]);
  useEffect(() => { try { localStorage.setItem('livedraw_confetti', String(confettiEnabled)); } catch {} }, [confettiEnabled]);

  useEffect(() => {
    loadRecentWinners();
    loadStats();
    loadWheelData();
  }, []);

  const loadRecentWinners = async () => {
    try {
      const { data } = await api.get('/public/winners');
      setRecentWinners(data.slice(0, 5));
    } catch (error) {
      console.error('Failed to load winners');
    }
  };

  const loadStats = async () => {
    try {
      const { data } = await api.get('/public/statistics');
      setStats({ remaining_entries: data.remaining_entries, total_winners: data.total_winners });
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const loadWheelData = async () => {
    setWheelDataLoading(true);
    try {
      const [entriesResponse, prizesResponse] = await Promise.all([
        api.get('/entries', { params: { status: 'Active' } }),
        api.get('/prizes', { params: { active_only: true } }),
      ]);

      const entries = Array.isArray(entriesResponse.data)
        ? entriesResponse.data.filter((entry) => entry.status === 'Active')
        : [];
      const prizes = Array.isArray(prizesResponse.data)
        ? prizesResponse.data.filter(
            (prize) => prize.is_active && Number(prize.remaining_quantity) > 0
          )
        : [];

      setParticipantEntries(entries);
      setAvailablePrizes(prizes);
    } catch (error) {
      console.error('Failed to load transparent wheel catalogues');
    } finally {
      setWheelDataLoading(false);
    }
  };

  const armAudio = () => {
    if (!soundEnabled) return null;

    try {
      const AudioContextClass =
        window.AudioContext || window.webkitAudioContext;

      if (!AudioContextClass) return null;

      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContextClass();

        const compressor = audioContextRef.current.createDynamicsCompressor();
        compressor.threshold.setValueAtTime(-14, audioContextRef.current.currentTime);
        compressor.knee.setValueAtTime(22, audioContextRef.current.currentTime);
        compressor.ratio.setValueAtTime(8, audioContextRef.current.currentTime);
        compressor.attack.setValueAtTime(0.003, audioContextRef.current.currentTime);
        compressor.release.setValueAtTime(0.28, audioContextRef.current.currentTime);
        compressor.connect(audioContextRef.current.destination);
        audioMasterRef.current = compressor;
      }

      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume().catch(() => {});
      }

      return audioContextRef.current;
    } catch {
      return null;
    }
  };

  const playTone = (
    frequency,
    duration = 0.08,
    type = 'sine',
    volume = 0.07,
    delaySeconds = 0
  ) => {
    if (!soundEnabled) return;

    const context = armAudio();
    if (!context) return;

    try {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const startTime = context.currentTime + delaySeconds;
      const endTime = startTime + duration;

      oscillator.type = type;
      oscillator.frequency.setValueAtTime(frequency, startTime);
      const volumeLevel = soundVolume / 100;
      const boostedVolume = Math.min(volume * 5 * volumeLevel, 0.78);
      gain.gain.setValueAtTime(0.0001, startTime);
      gain.gain.exponentialRampToValueAtTime(boostedVolume, startTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, endTime);

      oscillator.connect(gain);
      gain.connect(audioMasterRef.current || context.destination);
      oscillator.start(startTime);
      oscillator.stop(endTime + 0.02);
    } catch {}
  };

  const playDrumHit = (delaySeconds = 0, intensity = 1) => {
    const volume = 0.075 * intensity;
    playTone(112, 0.13, 'sine', volume, delaySeconds);
    playTone(58, 0.17, 'triangle', volume * 0.72, delaySeconds + 0.012);
  };

  const playDrumroll = (durationSeconds = 5, intensity = 1) => {
    if (!soundEnabled) return;

    const interval = 0.085;
    const hits = Math.floor(durationSeconds / interval);

    for (let index = 0; index < hits; index += 1) {
      const progress = index / Math.max(1, hits - 1);
      const accent = index % 4 === 0 ? 1.22 : 0.82;
      playDrumHit(
        index * interval,
        intensity * accent * (0.68 + progress * 0.48)
      );
    }
  };

  const playCymbalCrash = () => {
    if (!soundEnabled) return;

    const context = armAudio();
    if (!context) return;

    try {
      const duration = 1.7;
      const buffer = context.createBuffer(
        1,
        Math.floor(context.sampleRate * duration),
        context.sampleRate
      );
      const channel = buffer.getChannelData(0);

      for (let index = 0; index < channel.length; index += 1) {
        const decay = Math.pow(1 - index / channel.length, 2.4);
        channel[index] = (Math.random() * 2 - 1) * decay;
      }

      const source = context.createBufferSource();
      const filter = context.createBiquadFilter();
      const gain = context.createGain();
      const now = context.currentTime;
      const level = soundVolume / 100;

      source.buffer = buffer;
      filter.type = 'highpass';
      filter.frequency.setValueAtTime(1700, now);
      gain.gain.setValueAtTime(Math.min(0.62 * level, 0.72), now);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

      source.connect(filter);
      filter.connect(gain);
      gain.connect(audioMasterRef.current || context.destination);
      source.start(now);
      source.stop(now + duration);
    } catch {}
  };

  const playCountdownSound = (number) => {
    const frequencies = { 3: 440, 2: 520, 1: 620 };
    playTone(frequencies[number] || 780, 0.16, 'sine', 0.1);
  };

  const playTicketTick = () => {
    playTone(980, 0.025, 'square', 0.025);
  };

  const playPrizeTick = () => {
    playTone(620, 0.045, 'triangle', 0.045);
  };

  const playWinnerLock = () => {
    playTone(520, 0.16, 'sine', 0.08, 0);
    playTone(660, 0.18, 'sine', 0.08, 0.12);
    playTone(880, 0.28, 'sine', 0.1, 0.25);
  };

  const playGrandFanfare = () => {
    const fanfareNotes = [
      [523, 0.00, 'sawtooth', 0.12],
      [659, 0.00, 'triangle', 0.12],
      [784, 0.00, 'sine', 0.14],
      [659, 0.32, 'sawtooth', 0.13],
      [784, 0.32, 'triangle', 0.14],
      [1047, 0.32, 'sine', 0.17],
      [784, 0.68, 'sawtooth', 0.14],
      [1047, 0.68, 'triangle', 0.16],
      [1319, 0.68, 'sine', 0.19],
    ];

    fanfareNotes.forEach(
      ([frequency, delaySeconds, type, volume]) => {
        playTone(frequency, 0.62, type, volume, delaySeconds);
      }
    );

    playCymbalCrash();
  };

  const runAnimationWith = async (data) => {
    setDrawResult(null);
    setSelectedParticipant(null);
    setSelectedPrize(null);
    setParticipantDisplay(null);
    setPrizePreview(null);
    setParticipantProgress(0);

    const sourceEntries =
      Array.isArray(data.all_entries) && data.all_entries.length > 0
        ? data.all_entries.map((entry, index) => ({
            ...entry,
            id: entry.id || `draw-ticket-${index}`,
            status: 'Active',
          }))
        : participantEntries;

    const ticketPool = sourceEntries.length > 0
      ? shuffleItems(sourceEntries)
      : [{
          id: data.entry_id,
          sugo_id: data.sugo_id,
          username: data.username,
          status: 'Active',
        }];

    if (sourceEntries.length > 0) {
      setParticipantEntries(sourceEntries);
    }

    const selectedPrizeDetails =
      availablePrizes.find(
        (prize) =>
          String(prize.id) === String(data.prize_id) ||
          String(prize.name) === String(data.prize_name)
      ) || {
        id: data.prize_id,
        name: data.prize_name,
        image_url: '',
        remaining_quantity: null,
      };

    setDrawPhase('countdown');

    for (const number of [3, 2, 1]) {
      setCountdownValue(number);
      playCountdownSound(number);
      await wait(650);
    }

    setCountdownValue('GO');
    playTone(880, 0.25, 'sine', 0.12);
    await wait(450);
    setCountdownValue(null);

    setDrawPhase('participant-shuffle');
    setParticipantSpinning(true);
    playDrumroll(6.6, 1);

    for (let index = 0; index < ticketPool.length; index += 1) {
      const current = ticketPool[index];
      setParticipantDisplay(current);
      setParticipantProgress(index + 1);

      if (index % 12 === 0) {
        playTicketTick();
      }

      const remaining = ticketPool.length - index;
      const delay = remaining > 8
        ? Math.max(7, Math.floor(4400 / ticketPool.length))
        : 90 + ((8 - remaining) * 60);

      await wait(delay);
    }

    const winner = {
      entry_id: data.entry_id,
      sugo_id: data.sugo_id,
      username: data.username,
    };

    setParticipantSpinning(false);
    setParticipantDisplay(winner);
    setSelectedParticipant(winner);
    setParticipantProgress(ticketPool.length);
    playWinnerLock();

    await wait(900);

    setDrawPhase('prize-shuffle');
    setPrizeSpinning(true);
    playDrumroll(4.5, 0.78);

    const prizePool = availablePrizes.length > 0
      ? availablePrizes
      : [selectedPrizeDetails];

    const prizeSequence = [];
    for (let cycle = 0; cycle < 5; cycle += 1) {
      prizeSequence.push(...shuffleItems(prizePool));
    }

    for (let index = 0; index < prizeSequence.length; index += 1) {
      const currentPrize = prizeSequence[index];
      setPrizePreview(currentPrize);
      playPrizeTick();

      const remaining = prizeSequence.length - index;
      const delay = remaining > 7
        ? 100
        : 150 + ((7 - remaining) * 75);

      await wait(delay);
    }

    setPrizeSpinning(false);
    setPrizePreview(selectedPrizeDetails);
    setSelectedPrize(selectedPrizeDetails);
    setDrawPhase('reveal');
    setDrawResult(data);
    setIsDrawing(false);

    playGrandFanfare();

    if (confettiEnabled) {
      triggerConfetti();
    }
  };

  const handleStartDraw = async () => {
    if (isDrawing) return;
    // Ref-based guard against double-clicks / rapid refreshes (state updates are async)
    if (startingRef.current) return;
    startingRef.current = true;
    armAudio();
    setIsDrawing(true);
    setDrawResult(null);
    try {
      const { data } = await api.post('/draw/start');
      runAnimationWith(data);
    } catch (error) {
      const status = error.response?.status;
      if (status === 401) {
        toast.error('Your admin session expired. Please log in again.');
        setTimeout(() => { window.location.href = '/admin/login'; }, 1200);
      } else {
        toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to start draw');
      }
      setIsDrawing(false);
    } finally {
      // Small delay to swallow rapid repeat clicks even after error toast
      setTimeout(() => { startingRef.current = false; }, 800);
    }
  };

  const handleConfirmDraw = async () => {
    if (!drawResult) return;
    // Ref-based guard against double-clicks / rapid refreshes
    if (confirmingRef.current) return;
    confirmingRef.current = true;
    setIsConfirming(true);
    try {
      await api.post('/draw/confirm', drawResult);
      toast.success(`Winner confirmed: ${drawResult.sugo_id}`);
      setDrawResult(null);
      setSelectedParticipant(null);
      setSelectedPrize(null);
      setParticipantDisplay(null);
      setPrizePreview(null);
      setParticipantProgress(0);
      setCountdownValue(null);
      setDrawPhase('ready');
      loadRecentWinners();
      loadStats();
      loadWheelData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to confirm draw');
    } finally {
      setIsConfirming(false);
      confirmingRef.current = false;
    }
  };

  const handleRedraw = async () => {
    if (redrawReason.trim().length < 3) {
      toast.error('Please provide a redraw reason (min 3 characters).');
      return;
    }
    // Rapid-repeat protection
    if (redrawingRef.current) return;
    redrawingRef.current = true;
    setIsRedrawSubmitting(true);
    setIsDrawing(true);
    try {
      const { data } = await api.post('/draw/redraw', { reason: redrawReason.trim(), previous: drawResult });
      toast.success('Redraw recorded — spinning new selection.');
      setRedrawOpen(false);
      setRedrawReason('');
      setDrawResult(null);
      runAnimationWith(data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to redraw');
      setIsDrawing(false);
    } finally {
      setIsRedrawSubmitting(false);
      // Release ref after a short cooldown so the button re-enables only when animation is safely started
      setTimeout(() => { redrawingRef.current = false; }, 800);
    }
  };

  const handleCancel = async () => {
    if (cancelReason.trim().length < 3) {
      toast.error('Please provide a cancellation reason (min 3 characters).');
      return;
    }
    if (cancellingRef.current) return;
    cancellingRef.current = true;
    setIsCancelSubmitting(true);
    try {
      await api.post('/draw/cancel', { reason: cancelReason.trim(), ...drawResult });
      toast.success('Draw cancelled and audited.');
      setCancelOpen(false);
      setCancelReason('');
      setDrawResult(null);
      setSelectedParticipant(null);
      setSelectedPrize(null);
      setParticipantDisplay(null);
      setPrizePreview(null);
      setParticipantProgress(0);
      setCountdownValue(null);
      setDrawPhase('ready');
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to cancel');
    } finally {
      setIsCancelSubmitting(false);
      setTimeout(() => { cancellingRef.current = false; }, 500);
    }
  };


  const triggerConfetti = () => {
    const container = document.getElementById('confetti-container');
    if (!container) return;

    container.innerHTML = '';
    const colors = ['#D4AF37', '#FFE57B', '#F3A6C8', '#FFFFFF', '#E44792'];

    for (let index = 0; index < 120; index += 1) {
      const piece = document.createElement('div');
      piece.className = 'confetti';
      piece.style.left = Math.random() * 100 + '%';
      piece.style.backgroundColor =
        colors[Math.floor(Math.random() * colors.length)];
      piece.style.animationDelay = Math.random() * 1.8 + 's';
      piece.style.animationDuration = 2.6 + Math.random() * 2 + 's';
      piece.style.width = 5 + Math.random() * 9 + 'px';
      piece.style.height = 8 + Math.random() * 13 + 'px';
      container.appendChild(piece);
    }

    for (let burstIndex = 0; burstIndex < 10; burstIndex += 1) {
      const burst = document.createElement('div');
      burst.className = 'firework-burst';
      burst.style.left = 8 + Math.random() * 84 + '%';
      burst.style.top = 8 + Math.random() * 52 + '%';
      burst.style.setProperty(
        '--burst-delay',
        `${burstIndex * 0.22 + Math.random() * 0.25}s`
      );

      for (let rayIndex = 0; rayIndex < 18; rayIndex += 1) {
        const particle = document.createElement('span');
        particle.style.setProperty('--ray-angle', `${rayIndex * 20}deg`);
        particle.style.setProperty(
          '--ray-distance',
          `${70 + Math.random() * 95}px`
        );
        particle.style.setProperty(
          '--ray-color',
          colors[(rayIndex + burstIndex) % colors.length]
        );
        burst.appendChild(particle);
      }

      container.appendChild(burst);
    }

    setTimeout(() => { container.innerHTML = ''; }, 7000);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen();
    else document.exitFullscreen();
  };

  const participantTicketSummary = useMemo(() => {
    const grouped = new Map();

    participantEntries.forEach((entry) => {
      const sugoId = String(entry.sugo_id || '').trim();
      if (!sugoId) return;

      const existing = grouped.get(sugoId) || {
        sugo_id: sugoId,
        username: '',
        tickets: 0,
      };

      existing.tickets += 1;
      if (!existing.username && entry.username) {
        existing.username = String(entry.username).trim();
      }

      grouped.set(sugoId, existing);
    });

    return Array.from(grouped.values()).sort(
      (left, right) =>
        right.tickets - left.tickets ||
        left.sugo_id.localeCompare(right.sugo_id)
    );
  }, [participantEntries]);

  const normalizedParticipantSearch = participantSearch.trim().toLowerCase();

  const filteredParticipantSummary = participantTicketSummary.filter(
    (participant) =>
      !normalizedParticipantSearch ||
      participant.sugo_id.toLowerCase().includes(normalizedParticipantSearch) ||
      participant.username.toLowerCase().includes(normalizedParticipantSearch)
  );


  return (
    <div className="min-h-screen bg-[#080808] text-white relative live-draw-page">
      <div id="confetti-container" className="fixed inset-0 pointer-events-none z-50"></div>

      {/* Header */}
      <div className="live-draw-header bg-gradient-to-r from-[#D4AF37]/20 to-[#F3A6C8]/20 border-b border-[#D4AF37]/30 px-8 py-6">
        <div className="max-w-[1920px] mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-1">CEOs Family Lucky Draw 2026</h1>
            <p className="text-[#D4AF37]">Live Draw System</p>
          </div>
          <div className="flex items-center gap-3 live-draw-controls">
            <div className="live-volume-control">
              <Volume2 className="w-4 h-4" />
              <input
                type="range"
                min="0"
                max="100"
                step="5"
                value={soundEnabled ? soundVolume : 0}
                onChange={(event) => {
                  const nextVolume = Number(event.target.value);
                  setSoundVolume(nextVolume);
                  setSoundEnabled(nextVolume > 0);
                  if (nextVolume > 0) armAudio();
                }}
                aria-label="Live draw sound volume"
                data-testid="input-sound-volume"
              />
              <strong>{soundEnabled ? soundVolume : 0}%</strong>
            </div>
            <Button
              onClick={() => {
                const nextEnabled = !soundEnabled;
                setSoundEnabled(nextEnabled);
                if (nextEnabled) {
                  armAudio();
                  playTone(880, 0.18, 'sine', 0.1);
                }
              }}
              variant="outline"
              size="sm"
              className={`border-[#D4AF37]/30 ${soundEnabled ? 'text-[#D4AF37]' : 'text-[#A0A0A0]'}`}
              data-testid="btn-toggle-sound"
              title={`Sound ${soundEnabled ? 'ON' : 'OFF'}`}
            >
              {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              <span className="ml-2 text-xs">{soundEnabled ? 'ON' : 'OFF'}</span>
            </Button>
            <Button
              onClick={() => setConfettiEnabled(!confettiEnabled)}
              variant="outline"
              size="sm"
              className={`border-[#F3A6C8]/30 ${confettiEnabled ? 'text-[#F3A6C8]' : 'text-[#A0A0A0]'}`}
              data-testid="btn-toggle-confetti"
              title={`Confetti ${confettiEnabled ? 'ON' : 'OFF'}`}
            >
              <Sparkles className="w-4 h-4" />
              <span className="ml-2 text-xs">{confettiEnabled ? 'ON' : 'OFF'}</span>
            </Button>
            <Button
              onClick={toggleFullscreen}
              variant="outline"
              size="sm"
              className="border-[#D4AF37]/30 text-[#D4AF37]"
              data-testid="btn-fullscreen"
            >
              <Maximize className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1920px] mx-auto px-8 py-12">
        {/* Luxury digital draw stage */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
          <Card className="digital-stage-card participant-stage-card">
            <div className="text-center mb-5">
              <Badge className="bg-[#D4AF37] text-[#080808] text-lg px-6 py-2">
                <Trophy className="w-5 h-5 mr-2 inline" />PARTICIPANT
              </Badge>
            </div>

            <DigitalParticipantStage
              phase={drawPhase}
              countdown={countdownValue}
              displayedParticipant={participantDisplay}
              progress={participantProgress}
              total={participantEntries.length || stats.remaining_entries}
              selectedParticipant={selectedParticipant}
            />

            <div className="digital-stage-note">
              <strong>
                {participantEntries.length || stats.remaining_entries}
              </strong>{' '}
              individual active tickets loaded
              <small>
                The digital scanner cycles the complete ticket pool.
                Repeated IDs remain separate valid tickets.
              </small>
            </div>
          </Card>

          <Card className="digital-stage-card prize-stage-card">
            <div className="text-center mb-5">
              <Badge className="bg-[#F3A6C8] text-[#080808] text-lg px-6 py-2">
                <Gift className="w-5 h-5 mr-2 inline" />PRIZE
              </Badge>
            </div>

            <DigitalPrizeVault
              phase={drawPhase}
              previewPrize={prizePreview}
              selectedPrize={selectedPrize}
            />

            <div className="digital-stage-note prize-stage-note">
              <strong>{availablePrizes.length}</strong> available prize types loaded
              <small>
                The gift vault opens only after the participant winner is locked.
              </small>
            </div>
          </Card>
        </div>

        {/* Controls */}
        <div className="text-center mb-12">
          {!drawResult ? (
            <Button
              onClick={handleStartDraw}
              disabled={isDrawing}
              className="px-16 py-8 text-2xl rounded-full bg-[#D4AF37] text-[#080808] hover:bg-[#F3A6C8] hover:text-[#080808] transition-colors duration-200 shadow-[0_0_40px_rgba(212,175,55,0.5)] hover:shadow-[0_0_60px_rgba(243,166,200,0.7)] disabled:opacity-50"
              data-testid="btn-start-live-draw"
            >
              <Zap className="w-8 h-8 mr-4" />
              {isDrawing ? 'DRAWING...' : 'START DRAW'}
            </Button>
          ) : (
            <div className="space-y-6">
              <div className="text-3xl font-bold text-[#D4AF37] mb-2 animate-pulse" data-testid="winner-announcement">
                WINNER SELECTED — Review below
              </div>
              <div className="flex flex-wrap justify-center gap-4">
                <Button
                  onClick={handleConfirmDraw}
                  disabled={isConfirming}
                  className="px-10 py-5 text-lg rounded-full bg-green-600 text-white hover:bg-green-700 transition-colors duration-200 disabled:opacity-50"
                  data-testid="btn-confirm-live-draw"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  {isConfirming ? 'Confirming...' : 'CONFIRM WINNER'}
                </Button>
                <Button
                  onClick={() => setRedrawOpen(true)}
                  disabled={isConfirming || isDrawing}
                  variant="outline"
                  className="px-8 py-5 text-lg rounded-full border-[#F3A6C8] text-[#F3A6C8] hover:bg-[#F3A6C8]/10"
                  data-testid="btn-open-redraw"
                >
                  <RefreshCw className="w-5 h-5 mr-2" />REDRAW
                </Button>
                <Button
                  onClick={() => setCancelOpen(true)}
                  disabled={isConfirming || isDrawing}
                  variant="outline"
                  className="px-8 py-5 text-lg rounded-full border-red-500 text-red-500 hover:bg-red-500/10"
                  data-testid="btn-open-cancel"
                >
                  <XCircle className="w-5 h-5 mr-2" />CANCEL
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Stats + Recent Winners */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Card className="bg-[#121212] border border-[#D4AF37]/20 p-6">
            <h3 className="text-xl font-bold text-white mb-4">Current Status</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className="text-4xl font-bold text-[#D4AF37]" data-testid="remaining-entries">{stats.remaining_entries}</div>
                <div className="text-sm text-[#A0A0A0] mt-1">Remaining Entries</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#F3A6C8]" data-testid="total-winners">{stats.total_winners}</div>
                <div className="text-sm text-[#A0A0A0] mt-1">Total Winners</div>
              </div>
            </div>
          </Card>
          <Card className="bg-[#121212] border border-[#F3A6C8]/20 p-6">
            <h3 className="text-xl font-bold text-white mb-4">Recent Winners</h3>
            <div className="space-y-2" data-testid="live-recent-winners">
              {recentWinners.length === 0 ? (
                <div className="text-center text-[#A0A0A0] py-4">No winners yet</div>
              ) : (
                recentWinners.map((winner, index) => (
                  <div key={index} className="flex items-center justify-between bg-[#080808] p-3 rounded-lg border border-[#D4AF37]/10">
                    <div>
                      <div className="text-white font-semibold">{winner.sugo_id}</div>
                      <div className="text-[#A0A0A0] text-sm">{winner.username || 'Anonymous'}</div>
                    </div>
                    <div className="text-[#F3A6C8] text-sm font-semibold text-right">{winner.prize}</div>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>

        {/* Participant ticket directory */}
        <Card className="participant-directory-card">
          <div className="participant-directory-header">
            <div>
              <div className="participant-directory-title">
                <Trophy className="w-5 h-5" />
                Participant Ticket List
              </div>
              <div className="participant-directory-subtitle">
                Every purchase equals one ticket. Duplicate IDs are counted separately.
              </div>
            </div>

            <div className="participant-directory-totals">
              <div>
                <strong>{participantTicketSummary.length}</strong>
                <span>Participants</span>
              </div>
              <div>
                <strong>{participantEntries.length}</strong>
                <span>Active Tickets</span>
              </div>
            </div>
          </div>

          <div className="participant-directory-toolbar">
            <input
              type="search"
              value={participantSearch}
              onChange={(event) => setParticipantSearch(event.target.value)}
              placeholder="Search SUGO ID or name…"
              className="participant-directory-search"
              data-testid="participant-ticket-search"
            />
            <span>
              Showing {filteredParticipantSummary.length} of {participantTicketSummary.length}
            </span>
          </div>

          <div
            className="participant-ticket-scroll"
            data-testid="participant-ticket-directory"
          >
            {filteredParticipantSummary.length === 0 ? (
              <div className="participant-directory-empty">
                {wheelDataLoading
                  ? 'Loading participant tickets…'
                  : participantSearch
                    ? 'No matching participant found.'
                    : 'No active participant tickets.'}
              </div>
            ) : (
              <div className="participant-ticket-grid">
                {filteredParticipantSummary.map((participant, index) => {
                  const share = participantEntries.length > 0
                    ? (participant.tickets / participantEntries.length) * 100
                    : 0;

                  return (
                    <div
                      key={participant.sugo_id}
                      className="participant-ticket-card"
                    >
                      <div className="participant-ticket-rank">
                        #{index + 1}
                      </div>

                      <div className="participant-ticket-identity">
                        <strong>{participant.sugo_id}</strong>
                        <span>{participant.username || 'SUGO participant'}</span>
                      </div>

                      <div className="participant-ticket-count">
                        <strong>{participant.tickets}</strong>
                        <span>{participant.tickets === 1 ? 'Ticket' : 'Tickets'}</span>
                      </div>

                      <div className="participant-ticket-share">
                        <div>
                          <span style={{ width: `${Math.max(share, 1)}%` }} />
                        </div>
                        <small>{share.toFixed(2)}% of active tickets</small>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Card>

      </div>

      {/* Redraw dialog */}
      <AlertDialog open={redrawOpen} onOpenChange={setRedrawOpen}>
        <AlertDialogContent className="bg-[#121212] border border-[#F3A6C8]/40 text-white" data-testid="dialog-redraw">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Redraw — provide a reason</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A0A0A0]">
              A redraw discards the current selection and picks a new random participant + prize. The reason is recorded in the audit log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Label className="text-white text-sm">Reason (required, min 3 chars)</Label>
            <Textarea
              value={redrawReason}
              onChange={(e) => setRedrawReason(e.target.value)}
              placeholder="e.g. winner not present in room"
              className="mt-2 bg-[#080808] border-[#F3A6C8]/30 text-white"
              data-testid="input-redraw-reason"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setRedrawReason('')} className="bg-[#080808] border-[#A0A0A0]/30 text-white hover:bg-[#121212]">
              Back
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRedraw}
              disabled={redrawReason.trim().length < 3 || isRedrawSubmitting}
              className="bg-[#F3A6C8] text-[#080808] hover:bg-[#D4AF37] disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="btn-confirm-redraw"
            >
              <RefreshCw className="w-4 h-4 mr-2" />{isRedrawSubmitting ? 'Submitting...' : 'Redraw'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel dialog */}
      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent className="bg-[#121212] border border-red-500/40 text-white" data-testid="dialog-cancel">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Cancel this draw — provide a reason</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A0A0A0]">
              Cancelling discards the current selection. No entries or prizes are consumed. The reason is recorded in the audit log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Label className="text-white text-sm">Reason (required, min 3 chars)</Label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="e.g. technical issue during draw"
              className="mt-2 bg-[#080808] border-red-500/30 text-white"
              data-testid="input-cancel-reason"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCancelReason('')} className="bg-[#080808] border-[#A0A0A0]/30 text-white hover:bg-[#121212]">
              Back
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              disabled={cancelReason.trim().length < 3 || isCancelSubmitting}
              className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="btn-confirm-cancel"
            >
              <XCircle className="w-4 h-4 mr-2" />{isCancelSubmitting ? 'Submitting...' : 'Cancel Draw'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LiveDraw;
